import { useState, useEffect } from 'react';
import { storePhotos } from '../lib/api';
import { toast } from 'sonner';
import ImageUploader from './ImageUploader';
import { MapPin, Camera, Trash2, Plus } from 'lucide-react';

export default function ShopLocationPhotos({ store, onSaved }) {
  const [loc, setLoc] = useState({ address: '', lat: '', lng: '', pincode: '' });
  const [photos, setPhotos] = useState([]);
  const [newPhoto, setNewPhoto] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!store) return;
    setLoc({
      address: store.address || '',
      lat: store.lat != null ? String(store.lat) : '',
      lng: store.lng != null ? String(store.lng) : '',
      pincode: store.pincode || '',
    });
    setPhotos(store.photos || []);
  }, [store?.id]);

  const useCurrentLocation = () => {
    if (!navigator.geolocation) return toast.error('Geolocation not supported');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLoc(l => ({ ...l, lat: pos.coords.latitude.toFixed(6), lng: pos.coords.longitude.toFixed(6) }));
        toast.success('Captured your current GPS');
      },
      () => toast.error('Could not get location (permission denied?)'),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const saveLocation = async () => {
    if (!store) return;
    setBusy(true);
    try {
      const payload = {
        address: loc.address || null,
        lat: loc.lat ? parseFloat(loc.lat) : null,
        lng: loc.lng ? parseFloat(loc.lng) : null,
        pincode: loc.pincode || null,
      };
      await storePhotos.updateLocation(store.id, payload);
      toast.success('Location saved');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const addPhoto = () => {
    if (!newPhoto) return;
    if (photos.length >= 8) return toast.error('Max 8 photos');
    setPhotos([...photos, newPhoto]);
    setNewPhoto('');
  };

  const removePhoto = (idx) => setPhotos(photos.filter((_, i) => i !== idx));

  const savePhotos = async () => {
    if (!store) return;
    setBusy(true);
    try {
      await storePhotos.updatePhotos(store.id, photos);
      toast.success(`Saved ${photos.length} photo${photos.length === 1 ? '' : 's'}`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const mapEmbedUrl = (loc.lat && loc.lng)
    ? `https://www.google.com/maps?q=${loc.lat},${loc.lng}&z=16&output=embed`
    : null;

  if (!store) return <div className="card-soft p-5 text-sm text-ink/60">Select a store to edit location and photos.</div>;

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-12 h-12 rounded-xl bg-[#FFF3E0] text-[#D35400] grid place-items-center shrink-0"><MapPin className="w-6 h-6"/></div>
        <div className="flex-1 min-w-[200px]">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#D35400]">Shop on the map</div>
          <h2 className="font-heading font-black text-2xl">Location & shop photos</h2>
        </div>
      </div>

      {/* Location */}
      <div className="card-soft p-5">
        <div className="font-heading font-bold text-base mb-3">Location</div>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Street address" testId="loc-address" full>
            <input value={loc.address} onChange={e=>setLoc({...loc, address:e.target.value})} className="lkinp" placeholder="Shop No. 12, Main Road, Rampur"/>
          </Field>
          <Field label="Pincode" testId="loc-pincode">
            <input value={loc.pincode} onChange={e=>setLoc({...loc, pincode:e.target.value.replace(/\D/g,'').slice(0,6)})} className="lkinp" placeholder="123456" inputMode="numeric"/>
          </Field>
          <div></div>
          <Field label="Latitude" testId="loc-lat">
            <input value={loc.lat} onChange={e=>setLoc({...loc, lat:e.target.value})} className="lkinp" placeholder="28.6139"/>
          </Field>
          <Field label="Longitude" testId="loc-lng">
            <input value={loc.lng} onChange={e=>setLoc({...loc, lng:e.target.value})} className="lkinp" placeholder="77.2090"/>
          </Field>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <button data-testid="use-current-loc" type="button" onClick={useCurrentLocation} className="pill !bg-white hover:border-[#E67E22] hover:text-[#D35400]"><MapPin className="w-3 h-3"/>Use my current GPS</button>
          <button data-testid="save-location-btn" disabled={busy} onClick={saveLocation} className="btn-primary !py-2 !px-4 text-sm">Save location</button>
        </div>
        {mapEmbedUrl && (
          <div className="mt-4 rounded-xl overflow-hidden border border-border" data-testid="map-embed">
            <iframe
              title="Shop location"
              src={mapEmbedUrl}
              width="100%"
              height="320"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        )}
      </div>

      {/* Photos */}
      <div className="card-soft p-5">
        <div className="flex items-center gap-2 mb-3">
          <Camera className="w-4 h-4 text-[#D35400]"/>
          <div className="font-heading font-bold text-base">Shop photos ({photos.length}/8)</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {photos.map((p, i) => (
            <div key={i} data-testid={`shop-photo-${i}`} className="relative aspect-square rounded-lg overflow-hidden border border-border bg-muted">
              <img src={p} alt={`Shop ${i+1}`} className="w-full h-full object-cover"/>
              <button onClick={() => removePhoto(i)} className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-white/90 hover:bg-red-50 text-red-600 grid place-items-center shadow-sm">
                <Trash2 className="w-3.5 h-3.5"/>
              </button>
            </div>
          ))}
          {photos.length === 0 && <div className="col-span-full text-xs text-ink/50 py-3">No photos yet. Add shop facade, interior, billboard, products etc.</div>}
        </div>
        <div className="rounded-lg border border-dashed border-border p-3 bg-[#FBF8F1]">
          <ImageUploader testId="shop-photo-uploader" label="shop photo" value={newPhoto} onChange={setNewPhoto}/>
          <div className="flex gap-2 mt-2">
            <button data-testid="add-shop-photo" disabled={!newPhoto} onClick={addPhoto} className="pill !bg-white disabled:opacity-50 hover:border-[#E67E22] hover:text-[#D35400]"><Plus className="w-3 h-3"/>Add to gallery</button>
            <button data-testid="save-shop-photos" disabled={busy} onClick={savePhotos} className="btn-primary !py-2 !px-4 text-sm ml-auto">Save photos</button>
          </div>
        </div>
      </div>

      <style>{`.lkinp{width:100%;border:1px solid hsl(var(--border));background:white;border-radius:0.5rem;padding:0.55rem 0.75rem;font-size:0.875rem;outline:none}.lkinp:focus{border-color:#E67E22;box-shadow:0 0 0 3px rgba(230,126,34,0.15)}`}</style>
    </div>
  );
}

function Field({ label, children, testId, full }) {
  return (
    <label data-testid={testId} className={`block ${full ? 'md:col-span-2' : ''}`}>
      <div className="text-xs uppercase tracking-[0.18em] font-bold text-ink/70 mb-1.5">{label}</div>
      {children}
    </label>
  );
}
